import { DateRangePicker } from '@/components/DateRangePicker/DateRangePicker'
import ItemCard from '@/components/ItemCard'
import Select from '@/components/Select'
import GrowthCircle from '@/icons/growthCircle.svg?react'
import DollarIcon from '@mui/icons-material/AttachMoney'
import { Box, CircularProgress } from '@mui/material'
import {
  DateField,
  ImageField,
  NumberField,
  Show,
  SimpleShowLayout,
  TextField
} from 'react-admin'
import { useParams } from 'react-router-dom'
import { Graphs, useSaleGraph } from '../hooks/useSaleGraph'
import { EarningsGraph } from './EarningsGraph'
import { ItemsSoldGraph } from './ItemsSoldGraph'

const styles = {
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    mb: 3
  }
}

const SaleShow = () => {
  const { id } = useParams()
  const {
    selectedGraph,
    chooseSelectedGraph,
    graphData,
    minStartDate,
    maxEndDate,
    startDate,
    endDate,
    fetchEarningsAndOrders,
    loading
  } = useSaleGraph(id)

  const estimatedEarnings = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(
    graphData?.estimatedEarnings !== undefined
      ? graphData.estimatedEarnings / 100
      : 0
  )

  return (
    <Show sx={{ mb: '70px' }} id={id}>
      <SimpleShowLayout>
        <Box
          sx={{
            w: '100%',
            mb: 5
          }}
        >
          <Box
            sx={{
              display: 'flex',
              maxWidth: ['66%', '66%', '66%', '66%', '50%'],
              maxHeight: '410px',
              boxShadow:
                '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)'
            }}
          >
            <ImageField
              source="mainImage"
              sx={{
                width: '50%',
                '& .RaImageField-image': {
                  width: '100%',
                  height: '100%'
                }
              }}
            />

            <Box sx={{ px: 3 }}>
              <TextField
                source="name"
                label="name"
                sx={{
                  fontSize: '1.125rem'
                }}
              />
              <dl>
                <dt style={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                  Sale Period:
                </dt>
                <dd style={{ marginLeft: 0 }}>
                  <DateField
                    source="activeAt"
                    options={{
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    }}
                  />
                  {' - '}
                  <DateField
                    source="expiresAt"
                    options={{
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    }}
                  />
                </dd>
              </dl>

              <dl style={{ fontSize: '0.875rem' }}>
                <dt
                  style={{
                    float: 'left',
                    fontWeight: 'bold',
                    marginRight: '4px'
                  }}
                >
                  Sale Price:
                </dt>
                <dd>
                  <NumberField
                    source="price"
                    options={{
                      style: 'currency',
                      currency: 'USD'
                    }}
                  />
                </dd>
                <dt
                  style={{
                    float: 'left',
                    fontWeight: 'bold',
                    marginRight: '4px'
                  }}
                >
                  MSRP:
                </dt>

                <dd style={{ marginLeft: 0 }}>
                  <NumberField
                    source="retailPrice"
                    options={{
                      style: 'currency',
                      currency: 'USD'
                    }}
                  />
                </dd>
                <dt
                  style={{
                    float: 'left',
                    fontWeight: 'bold',
                    marginRight: '4px'
                  }}
                >
                  Discount:
                </dt>
                <dd style={{ marginLeft: 0 }}>
                  <NumberField
                    source="promotionPercentage"
                    options={{
                      style: 'percent'
                    }}
                    transform={(v: number) => v / 100}
                  />
                </dd>
              </dl>
            </Box>
          </Box>
        </Box>

        {loading ? (
          <Box sx={styles.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <DateRangePicker
              minStartDate={minStartDate}
              maxEndDate={maxEndDate}
              startDate={startDate}
              endDate={endDate}
              runOnSubmit={fetchEarningsAndOrders}
              sx={{ marginBottom: '12px' }}
            />

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <ItemCard
                icon={DollarIcon}
                title="Estimated Earnings"
                data={estimatedEarnings}
                sx={{ flex: 1 }}
              />

              <ItemCard
                icon={GrowthCircle}
                title="Net Items Sold"
                data={graphData && graphData.netItemsSold}
                sx={{ flex: 1 }}
              />
            </Box>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', px: 3 }}
            >
              <strong style={{ fontSize: '1.25rem' }}>Performance</strong>

              <Select
                options={['Earnings', 'Items Sold']}
                value={selectedGraph}
                onChange={(value) => chooseSelectedGraph(value as Graphs)}
              />
            </Box>
            {selectedGraph === 'Earnings' && graphData && (
              <EarningsGraph graphData={graphData} />
            )}
            {selectedGraph === 'Items Sold' && graphData && (
              <ItemsSoldGraph graphData={graphData} />
            )}
          </>
        )}
      </SimpleShowLayout>
    </Show>
  )
}

export { SaleShow }
